import haversine from "haversine-distance";
import {useState} from "react";
import toast from "react-hot-toast";
import {useIntl} from "react-intl";
import {getLocationSuggestions, reverseGeocode} from "@/api/apiClient";
import {useEnrichLocation} from "@/features/location-suggestions";
import type {LocationWithCoordinates} from "@/features/location-suggestions";
import {useGeolocation} from "./useGeolocation";

export type UseCurrentLocationParams = {
    snapDistanceInMeters?: number;
};

export function useCurrentLocation({snapDistanceInMeters = 100}: UseCurrentLocationParams = {}) {
    const [isLoading, setIsLoading] = useState(false);
    const intl = useIntl();
    const {enrichLocation} = useEnrichLocation();
    const {getGeolocation} = useGeolocation();

    const showErrorToast = () => {
        toast.error(
            intl.formatMessage({
                id: "geolocation_failed",
                defaultMessage:
                    "Unable to get your current location. Please enter your address manually.",
            })
        );
    };

    const getCurrentLocation = async (): Promise<LocationWithCoordinates | null> => {
        setIsLoading(true);

        const {coordinates, error} = await getGeolocation();

        if (!coordinates) {
            setIsLoading(false);
            // Permission denied modal is shown by useGeolocation, show toast for other errors
            if (error?.type !== "permission_denied") {
                showErrorToast();
            }
            return null;
        }

        try {
            const pickups = await getLocationSuggestions({
                serviceType: "TAXI",
                query: "",
                addressType: "PICKUP",
            });

            const mainPickup = pickups.suggestions.find(
                (s) => s.location.type === "PREDEFINED_PLACE" && s.location.id === "MAIN_PICKUP"
            );

            if (mainPickup) {
                const enrichedMainPickup = await enrichLocation(mainPickup.location);
                if (
                    haversine(
                        {lat: coordinates.lat, lon: coordinates.lon},
                        {lat: enrichedMainPickup.lat, lon: enrichedMainPickup.lon}
                    ) < snapDistanceInMeters
                ) {
                    setIsLoading(false);
                    return enrichedMainPickup;
                }
            }

            const userLocation = await reverseGeocode({coordinates});

            setIsLoading(false);
            // reverseGeocode always returns coordinates when called with coordinates
            return userLocation as LocationWithCoordinates;
        } catch (err) {
            setIsLoading(false);
            console.error("Error enriching location:", err);
            showErrorToast();
            return null;
        }
    };

    return {getCurrentLocation, isLoading};
}
